import React from "react";

/**
 * Lightweight markdown renderer for AI-generated text.
 * Handles: **bold**, *italic*, - lists, numbered lists, line breaks.
 */
export const MarkdownText = ({ content, className = "" }) => {
  if (!content) return null;

  const lines = content.split("\n");
  const elements = [];
  let listItems = [];
  let listType = null;
  let key = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      if (listType === "ul") {
        elements.push(
          <ul key={key++} className="space-y-1.5 my-2 ml-1">
            {listItems}
          </ul>
        );
      } else {
        elements.push(
          <ol key={key++} className="space-y-1.5 my-2 ml-1 list-decimal list-inside">
            {listItems}
          </ol>
        );
      }
      listItems = [];
      listType = null;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      elements.push(<div key={key++} className="h-2" />);
      continue;
    }

    // Unordered list item (- or *)
    const ulMatch = trimmed.match(/^[-*]\s+(.+)/);
    if (ulMatch) {
      if (listType !== "ul") flushList();
      listType = "ul";
      listItems.push(
        <li key={key++} className="flex gap-2 items-start">
          <span className="text-gray-400 mt-0.5 shrink-0">&#8226;</span>
          <span>{renderInline(ulMatch[1])}</span>
        </li>
      );
      continue;
    }

    // Ordered list item (1. 2. etc)
    const olMatch = trimmed.match(/^(\d+)[.)]\s+(.+)/);
    if (olMatch) {
      if (listType !== "ol") flushList();
      listType = "ol";
      listItems.push(
        <li key={key++} className="flex gap-2 items-start">
          <span className="text-gray-500 font-medium shrink-0">{olMatch[1]}.</span>
          <span>{renderInline(olMatch[2])}</span>
        </li>
      );
      continue;
    }

    flushList();
    elements.push(
      <p key={key++} className="my-0.5">
        {renderInline(trimmed)}
      </p>
    );
  }

  flushList();

  return <div className={`text-sm leading-relaxed text-gray-700 ${className}`}>{elements}</div>;
};

/** Render inline markdown: **bold**, *italic* */
function renderInline(text) {
  if (!text) return null;

  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Find **bold**
    const boldIdx = remaining.indexOf("**");
    if (boldIdx !== -1) {
      const boldEnd = remaining.indexOf("**", boldIdx + 2);
      if (boldEnd !== -1) {
        if (boldIdx > 0) {
          parts.push(<React.Fragment key={key++}>{processItalic(remaining.substring(0, boldIdx), key)}</React.Fragment>);
          key += 10;
        }
        parts.push(
          <strong key={key++} className="font-semibold text-gray-800">
            {remaining.substring(boldIdx + 2, boldEnd)}
          </strong>
        );
        remaining = remaining.substring(boldEnd + 2);
        continue;
      }
    }

    // No more bold, process italic on remaining
    parts.push(<React.Fragment key={key++}>{processItalic(remaining, key)}</React.Fragment>);
    break;
  }

  return parts;
}

function processItalic(text, startKey) {
  const parts = [];
  let remaining = text;
  let key = startKey || 0;

  while (remaining.length > 0) {
    const italicIdx = remaining.indexOf("*");
    if (italicIdx !== -1 && remaining[italicIdx + 1] !== "*") {
      const italicEnd = remaining.indexOf("*", italicIdx + 1);
      if (italicEnd !== -1 && remaining[italicEnd - 1] !== "*") {
        if (italicIdx > 0) {
          parts.push(<React.Fragment key={key++}>{remaining.substring(0, italicIdx)}</React.Fragment>);
        }
        parts.push(
          <em key={key++} className="italic text-gray-600">
            {remaining.substring(italicIdx + 1, italicEnd)}
          </em>
        );
        remaining = remaining.substring(italicEnd + 1);
        continue;
      }
    }

    parts.push(<React.Fragment key={key++}>{remaining}</React.Fragment>);
    break;
  }

  return parts;
}

export default MarkdownText;
